import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FolderKanban, AlertTriangle, Clock, Ban, ListTodo } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const CHART = ["#8B5CF6", "#2563EB", "#EF4444", "#F59E0B", "#22C55E"];

export default function PmDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["pm-dashboard"], queryFn: () => get("/pm/dashboard") });

  if (isLoading || !data) {
    return <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;
  }
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="PM Overview" subtitle="Delivery health across every project." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard testid="pm-kpi-active" variant="hero" accent="blue" label="Active Projects" value={k.active_projects} sub="in delivery" icon={FolderKanban} />
        <KpiCard testid="pm-kpi-atrisk" variant="hero" accent="red" label="At Risk" value={k.at_risk} sub="amber/red health" icon={AlertTriangle} />
        <KpiCard testid="pm-kpi-overdue" accent="amber" label="Overdue Tickets" value={k.overdue_tickets} sub="past due date" icon={Clock} />
        <KpiCard testid="pm-kpi-blocked" accent="red" label="Blocked Tickets" value={k.blocked_tickets} sub="need unblocking" icon={Ban} />
        <KpiCard testid="pm-kpi-open" accent="primary" label="Open Items" value={k.total_open_items} sub="across all boards" icon={ListTodo} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Velocity" testid="pm-dashboard-velocity">
            <div className="p-4" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.velocity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="sprint" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="points" fill="#22C55E" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
          <Section title="Active Projects" testid="pm-dashboard-projects">
            <div className="divide-y divide-border">
              {data.projects.map((p) => (
                <button key={p.id} onClick={() => navigate(`/pm/projects/${p.id}`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50">
                  <span className="flex-1 text-sm font-medium">{p.name}</span>
                  <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${p.progress}%` }} /></div>
                  <StatusBadge family={p.health} label={p.health} />
                </button>
              ))}
              {data.projects.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No active projects.</div>}
            </div>
          </Section>
        </div>

        <Section title="Tickets by Type" testid="pm-dashboard-by-type">
          <div className="p-4" style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.by_type.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap gap-2">
              {data.by_type.map((t, i) => (
                <span key={t.type} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: CHART[i % CHART.length] }} />{t.type}
                </span>
              ))}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
